/**
 * Main Application Router
 * 
 * SECURITY: This file implements organization-based routing.
 * Users are automatically routed to their organization's dashboard.
 * 
 * - Nationwide Capital users → Settlement Dashboard
 * - DigniFi users → Welcome Agent Dashboard
 * - UnifiPro admin users → Can access both (default: Settlement)
 * 
 * All data access is ALSO enforced server-side. This client-side routing
 * is for UX purposes only - the API will reject unauthorized data requests.
 */

import { SignedIn, SignedOut, RedirectToSignIn, useUser } from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, Redirect } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Dashboard from "./pages/Dashboard";
import WelcomeDashboard from "./pages/WelcomeDashboard";
import Placements from "./pages/Placements";
import AgentMetrics from "./pages/AgentMetrics";
import Accounts from "./pages/Accounts";
import CallRecords from "./pages/CallRecords";
import FPDAnalysis from "./pages/FPDAnalysis";
import DealerPerformance from "./pages/DealerPerformance";
import FPDStats from "./pages/FPDStats";
import PositiveFeedback from "./pages/PositiveFeedback";
import ComplianceReports from "./pages/ComplianceReports";
import Payments from "./pages/Payments";
import PlacementDetail from "./pages/PlacementDetail";
import CallProcessing from "./pages/CallProcessing";
import Eva from "./pages/Eva";
import Campaigns from "./pages/Campaigns";
import LoanServicing from "./pages/LoanServicing";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentDeclined from "./pages/PaymentDeclined";
import PaymentPortal from "@/pages/PaymentPortal";
import ReviewAndPay from "@/pages/ReviewAndPay";
import InvestorLogin from "./pages/investor/InvestorLogin";
import InvestorDashboard from "./pages/investor/InvestorDashboard";
import InvestorSchedule from "./pages/investor/InvestorSchedule";
import InvestorCashFlow from "./pages/investor/InvestorCashFlow";
import InvestorNotifications from "./pages/investor/InvestorNotifications";
import InvestorAdmin from "./pages/investor/InvestorAdmin";
import DashboardLayout from "./components/DashboardLayout";
import { Loader2 } from "lucide-react";
import { useOrganizationContext } from "./hooks/useOrganization";

/**
 * Loading screen shown while authentication state is being determined
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

/**
 * No organization access screen
 * Shown when user is authenticated but has no organization membership
 */
function NoOrganizationAccess() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
        <div className="h-16 w-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
          <span className="text-3xl">🔒</span>
        </div>
        <h1 className="text-2xl font-bold">No Organization Access</h1>
        <p className="text-muted-foreground">
          Your account is not associated with any organization. 
          Please contact your administrator to be added to an organization.
        </p>
        <p className="text-sm text-muted-foreground mt-4">
          If you believe this is an error, please contact support at{" "}
          <a href="mailto:support@unifipro.ai" className="text-primary hover:underline">
            support@unifipro.ai
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Protected route wrapper - requires Clerk authentication AND organization membership
 */
function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isLoaded, isSignedIn } = useUser();
  const { isLoading: orgLoading, hasOrganization } = useOrganizationContext();
  
  // Still loading Clerk auth state
  if (!isLoaded) {
    return <LoadingScreen />;
  }
  
  // Not signed in - redirect to Clerk sign in
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }
  
  // Loading organization data
  if (orgLoading) {
    return <LoadingScreen />;
  }
  
  // Signed in but no organization - show access denied
  if (!hasOrganization) {
    return <NoOrganizationAccess />;
  }
  
  return <Component />;
}

/**
 * Organization-aware dashboard router
 * Routes users to the correct dashboard based on their organization
 */
function OrganizationDashboard() {
  const { dashboardType, isLoading, features, loanServicingOnly } = useOrganizationContext();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  // Loan Servicing-only orgs go directly to the servicing page
  if (loanServicingOnly) {
    return <Redirect to="/servicing" />;
  }
  
  // Route based on organization's dashboard type
  if (dashboardType === 'welcome_agent' && features.welcomeAgent) {
    return <WelcomeDashboard />;
  }
  
  // Default to settlement dashboard
  if (features.settlements) {
    return <Dashboard />;
  }
  
  // No access to any dashboard
  return <NoOrganizationAccess />;
}

/**
 * Settlement-only route guard
 * Only allows access if organization has settlement feature enabled
 */
function SettlementRoute({ component: Component }: { component: React.ComponentType }) {
  const { isLoaded, isSignedIn } = useUser();
  const { isLoading, hasOrganization, features } = useOrganizationContext();
  
  if (!isLoaded || isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }
  
  if (!hasOrganization) {
    return <NoOrganizationAccess />;
  }
  
  // Check if organization has access to settlement features
  if (!features.settlements) {
    return <Redirect to="/" />;
  }
  
  return <Component />;
}

/**
 * Welcome Agent route guard
 * Only allows access if organization has welcome agent feature enabled
 */
function WelcomeAgentRoute({ component: Component }: { component: React.ComponentType }) {
  const { isLoaded, isSignedIn } = useUser();
  const { isLoading, hasOrganization, features } = useOrganizationContext();
  
  if (!isLoaded || isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }
  
  if (!hasOrganization) {
    return <NoOrganizationAccess />;
  }
  
  // Check if organization has access to welcome agent features
  if (!features.welcomeAgent) {
    return <Redirect to="/" />;
  }
  
  return <Component />;
}

function InvestorAdminPage() {
  return (
    <DashboardLayout>
      <InvestorAdmin />
    </DashboardLayout>
  );
}

function Router() {
  return (
    <Switch>
      {/* Main dashboard - routes based on organization */}
      <Route path={"/"}>
        <ProtectedRoute component={OrganizationDashboard} />
      </Route>
      
      {/* Settlement Dashboard Routes - Nationwide Capital */}
      <Route path={"/placements"}>
        <SettlementRoute component={Placements} />
      </Route>
      <Route path={"/agent"}>
        <SettlementRoute component={AgentMetrics} />
      </Route>
      <Route path={"/accounts"}>
        <SettlementRoute component={Accounts} />
      </Route>
      <Route path={"/calls"}>
        <ProtectedRoute component={CallRecords} />
      </Route>
      <Route path={"/payments"}>
        <SettlementRoute component={Payments} />
      </Route>
      <Route path={"/placement/:id"}>
        <SettlementRoute component={PlacementDetail} />
      </Route>
      
      {/* Eva AI Assistant - Available to all authenticated users */}
      <Route path={"/eva"}>
        <ProtectedRoute component={Eva} />
      </Route>
      
      {/* Welcome Agent Routes - DigniFi */}
      <Route path={"/welcome"}>
        <WelcomeAgentRoute component={WelcomeDashboard} />
      </Route>
      <Route path={"/fpd"}>
        <WelcomeAgentRoute component={FPDAnalysis} />
      </Route>
      <Route path={"/fpd-stats"}>
        <WelcomeAgentRoute component={FPDStats} />
      </Route>
      <Route path={"/dealers"}>
        <WelcomeAgentRoute component={DealerPerformance} />
      </Route>
      <Route path={"/feedback"}>
        <WelcomeAgentRoute component={PositiveFeedback} />
      </Route>
      <Route path={"/compliance"}>
        <WelcomeAgentRoute component={ComplianceReports} />
      </Route>
      <Route path={"/call-processing"}>
        <WelcomeAgentRoute component={CallProcessing} />
      </Route>
      <Route path={"/campaigns"}>
        <WelcomeAgentRoute component={Campaigns} />
      </Route>
      <Route path={"/servicing"}>
        <WelcomeAgentRoute component={LoanServicing} />
      </Route>
      
      {/* Investor Admin — admin-only, inside main Clerk auth */}
      <Route path="/investor-admin">
        <ProtectedRoute component={InvestorAdminPage} />
      </Route>

      {/* Investor Portal — unlisted, separate auth from main dashboard */}
      <Route path="/investor/login" component={InvestorLogin} />
      <Route path="/investor/dashboard" component={InvestorDashboard} />
      <Route path="/investor/schedule" component={InvestorSchedule} />
      <Route path="/investor/cashflow" component={InvestorCashFlow} />
      <Route path="/investor/notifications" component={InvestorNotifications} />
      <Route path="/investor">
        <Redirect to="/investor/login" />
      </Route>

      {/* Consumer Payment Portal (public - no auth required) */}
      {/* /review: Pre-configured payment link from settlement agent (Railway) */}
      <Route path="/review" component={ReviewAndPay} />
      <Route path="/pay" component={PaymentPortal} />
      <Route path="/payment/success" component={PaymentSuccess} />
      <Route path="/payment/declined" component={PaymentDeclined} />

      {/* 404 */}
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
